import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';

const MultiSelect = ({ 
  options = [], 
  value = [], 
  onChange, 
  placeholder = "Select items...", 
  className,
  maxDisplayItems = 2,
  renderOption,
  renderValue,
  icon: Icon,
  iconClassName = "",
  dataTestid,
  optionTestidPrefix
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    if (typeof option === 'string') {
      return option.toLowerCase().includes(searchLower);
    }
    return (option.label || option.name || option.code || '').toLowerCase().includes(searchLower);
  }).sort((a, b) => {
    // Keep "All" or similar special options at the top
    const aValue = typeof a === 'string' ? a : (a.code || a.label || a.name || '');
    const bValue = typeof b === 'string' ? b : (b.code || b.label || b.name || '');
    
    if (aValue === 'All' || aValue.toLowerCase().includes('all')) return -1;
    if (bValue === 'All' || bValue.toLowerCase().includes('all')) return 1;
    
    // Check if both values are numeric - sort numerically
    const aNum = parseInt(aValue, 10);
    const bNum = parseInt(bValue, 10);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }
    
    return aValue.localeCompare(bValue);
  });

  const handleToggleOption = (option) => {
    const optionValue = typeof option === 'string' ? option : (option.value || option.code || option.id);
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleRemoveItem = (itemToRemove, e) => {
    e.stopPropagation();
    const newValue = value.filter(v => v !== itemToRemove);
    onChange(newValue);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const selectedOption = options.find(opt => {
        const optValue = typeof opt === 'string' ? opt : (opt.value || opt.code || opt.id);
        return optValue === value[0];
      });
      if (renderValue && selectedOption) return renderValue(selectedOption);
      if (typeof selectedOption === 'string') return selectedOption;
      return selectedOption?.label || selectedOption?.name || selectedOption?.code || value[0];
    }
    if (value.length <= maxDisplayItems) {
      return value.map(val => {
        const option = options.find(opt => {
          const optValue = typeof opt === 'string' ? opt : (opt.value || opt.code || opt.id);
          return optValue === val;
        });
        if (renderValue && option) return renderValue(option);
        if (typeof option === 'string') return option;
        return option?.label || option?.name || option?.code || val;
      }).join(', ');
    }
    return `${value.length} items selected`;
  };

  return (
    <div className={cn("relative", className)}>
      <div
        ref={triggerRef}
        data-testid={dataTestid}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm cursor-pointer shadow-sm",
          "focus:outline-none focus:ring-1 focus:ring-ring",
          isOpen && "ring-1 ring-ring"
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {Icon && <Icon className={cn("h-4 w-4 flex-shrink-0", iconClassName)} />}
          <div className="flex flex-wrap gap-1 flex-1 min-w-0">
            {value.length <= maxDisplayItems ? (
              <span className="truncate">
                {getDisplayText()}
              </span>
            ) : (
            <div className="flex flex-wrap gap-1">
              {value.slice(0, maxDisplayItems).map(val => {
                const option = options.find(opt => {
                  const optValue = typeof opt === 'string' ? opt : (opt.value || opt.code || opt.id);
                  return optValue === val;
                });
                const displayText = renderValue && option 
                  ? renderValue(option) 
                  : (typeof option === 'string' ? option : (option?.label || option?.name || option?.code || val));
                
                return (
                  <span
                    key={val}
                    className="inline-flex items-center gap-1 bg-accent text-accent-foreground text-xs px-2 py-1 rounded-md"
                  >
                    {displayText}
                    <X 
                      className="h-3 w-3 cursor-pointer hover:text-foreground" 
                      onClick={(e) => handleRemoveItem(val, e)}
                    />
                  </span>
                );
              })}
              {value.length > maxDisplayItems && (
                <span className="text-xs text-muted-foreground px-2 py-1">
                  +{value.length - maxDisplayItems} more
                </span>
              )}
            </div>
          )}
          </div>
        </div>
        <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform flex-shrink-0", isOpen && "rotate-180")} />
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-60 overflow-hidden text-popover-foreground"
        >
          <div className="p-2 border-b border-border">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-input rounded bg-transparent placeholder:text-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">No options found</div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = typeof option === 'string' ? option : (option.value || option.code || option.id);
                const isSelected = value.includes(optionValue);
                const displayText = renderOption 
                  ? renderOption(option) 
                  : (typeof option === 'string' ? option : (option.label || option.name || option.code));

                return (
                  <div
                    key={`${optionValue}-${index}`}
                    data-testid={optionTestidPrefix ? `${optionTestidPrefix}-${optionValue}` : undefined}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground",
                      isSelected && "bg-accent text-accent-foreground"
                    )}
                    onClick={() => handleToggleOption(option)}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-4 h-4 border rounded-sm",
                      isSelected ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"
                    )}>
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <span className="flex-1">{displayText}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;